import { useCallback, useEffect, useMemo, useState } from "react";
import usePageState from "../../../../utils/customHooks/usePageState";
import dayjs from "dayjs";
import useColumn from "../../../../utils/customHooks/useColumns";
import {
  convertResponseDate,
  fieldTruePermission,
  formatPayloadDate,
  prepareDefaultValues,
  prepareInitialConfig
} from "../../../../utils/formHelper";
import { useForm } from "react-hook-form";
import { STAUTES } from "../../../../utils/constant";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { getApi, postApi, deleteApi, putApi } from "../../../../utils/services";
import { useParams } from "react-router-dom";
import {
  GET_ALL_RTI_EMPLOYEERAPASSIGNED,
  GET_ALL_RTI_TRACKER,
  DELETE_RTI_PROGRESS,
  ADD_RTI_PROGRESS,
  UPDATE_RTI_PROGRESS,
  GET_EMPLOYEERAP_RTI_DROPDOWN,
  GET_RTI_PROGRESS_BY_ID,
  GET_CURRENT_COMPLETION
} from "../../../../utils/services/apiPath";
import { ProSelectField, DateField, PercentField, ProTextInput, ProGrid, NameCell, DeleteCell, TabGridAndFormLayout, dateColumnDef} from "@proscore-ai/proscore-ui-library";
import useCsvExport from "../../../../utils/useCsvExporter";

const rapRtiFormConfing = {
  rapTitle: {
    label: "RAP Title"
  },
  currentProject: {
    label: "Current Project"
  },
  rtiDescription: {
    label: "RAP RTI",
    rules: { required: "RAP RTI is required!" }
  },
  currentCompletionRate: {
    label: "Current Completion Rate"
  },
  progressPercentage: {
    label: "Progress Percentage",
    type: "number",
    rules: { required: "Progress Percentege is required!" }
  },
  timeinCourse: {
    label: "Time In Course",
    type: "number",
    rules: { required: "Time In Course is required!" }
  },
  progressDate: {
    label: "Progress Date",
    rules: { required: "Progress Date is required!" },
    defaultValue: dayjs()
  }
};

export default function RtiTracker() {
  const { id } = useParams();
  const [showGrid, setShowGrid] = useState(true);
  const [rapRtiData, setRapRtiData] = useState({});
  const [actionData, setActionData] = useState({});
  const [isAddMode, setIsAddMode] = useState(false);
  const [rapRtiDropDownData, setRapRtiDropDownData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const {
    currentPage,
    pageSize,
    data: rtisData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();

  const columns = useColumn(
    [
      {
        field: "rtiTitle",
        headerName: "RAP RTI ",
        renderCell: params => <NameCell params={params} onClick={onRtiClick} />
      },
      dateColumnDef({
        field: "progressDate",
        headerName: "Progress Date"
      }),
      {
        field: "progressPercentage",
        headerName: "Progress Percentage"
      },
      {
        field: "timeInCourse",
        headerName: "Time In Course"
      },
      {
        field: "rtiProgressTrackerGuid",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => <DeleteCell params={params} onDelete={onDelete} />
      }
    ],
    true
  );

  const [formConfig, setFormConfig] = useState(prepareInitialConfig(rapRtiFormConfing));
  const { handleSubmit, control, setValue, watch, getValues } = useForm({ defaultValues: prepareDefaultValues(rapRtiFormConfing) });
  const isEditModeOn = useMemo(() => Object.values(formConfig).some(({ mode }) => mode === "edit"), [formConfig]);

  const selectedRTI = watch("rtiDescription") || getValues().rtiDescription;

  const onEdit = () => {
    setFormConfig(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: { ...prev[key], mode: "edit" } }), {}));
  };

  const handleReadValueClick = name => {
    setFormConfig(prev => ({ ...prev, [name]: { ...prev[name], mode: "edit" } }));
  };

  const resetForm = () => {
    setActionData({});
    setShowGrid(true);
    setFormConfig(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: { ...prev[key], mode: "read" } }), {}));
  };

  const onCancel = () => {
    resetForm();
  };

  const onRtiClick = useCallback(async params => {
    setActionData(params.row);
    const parameters = params.row;
    const res = await getApi(GET_RTI_PROGRESS_BY_ID + parameters.rtiProgressTrackerGuid);
    const res1 = await getApi(`${GET_ALL_RTI_EMPLOYEERAPASSIGNED}/${id}`);
    const data = {
      rtiDescription: res?.raprtiGuid,
      progressPercentage: res?.progressPercentage,
      timeinCourse: res?.timeInCourse,
      progressDate: convertResponseDate(res?.progressDate),
      rapTitle: res1?.approvedJobTitle,
      currentProject: res1?.projectName
    };
    setRapRtiData(data);
    getAllCurrentCompletionRate(res?.raprtiGuid);
    Object.keys(formConfig).forEach(field => setValue(field, data[field]));
    setShowGrid(false);
  });

  const addButtonAction = () => {
    Object.keys(formConfig).forEach(field => setValue(field, null));
    onEdit();
    setShowGrid(false);
    setIsAddMode(true);
    getAllRapAssigned();
  };

  const onSave = async data => {
    if (isSaving) return;
    setIsSaving(true);
    setStatus(STAUTES.LOADING);
    const payload = {
      ...data,
      raprtiGuid: data?.rtiDescription,
      employeeGuid: id,
      progressDate: formatPayloadDate(data?.progressDate),
      progressPercentage: data?.progressPercentage,
      timeinCourse: data?.timeinCourse,
      rtiProgressTrackerGuid: actionData?.rtiProgressTrackerGuid ?? null
    };
    const methodApi = isAddMode ? postApi : putApi;
    const { error } = await methodApi(isAddMode ? ADD_RTI_PROGRESS : UPDATE_RTI_PROGRESS, payload);
    setIsSaving(false);
    if (error) return errorToast(error);
    successToast(`Partners Documents successfully.`);
    setStatus(STAUTES.IDLE);
    getAllRapRti();
    setShowGrid(true);
  };

  const onDelete = async id => {
    const { error } = await deleteApi(`${DELETE_RTI_PROGRESS}?rtiProgressTrackerId=${id}`);
    if (error) return errorToast(error);
    getAllRapRti();
    setShowGrid(true);
    successToast("RAP RTI deleted successfully.");
  };

  const getAllRapRti = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const payload = {
      pageIndex: searchString ? 1 : currentPage + 1,
      pageSize: pageSize,
      searchString: searchString,
      orderByAscending: true,
      orderCol: "",
      ...sortModel
    };
    const { data, totalRecords, error } = await postApi(GET_ALL_RTI_TRACKER, payload, { params: { employeeId: id } });
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  const handleDownloadCSV = useCsvExport({
    api: () => `${GET_ALL_RTI_TRACKER}?employeeId=${id}`,

    getPayload: () => {
      if (!id) {
        errorToast("Employee not found for export.");
        return null;
      }

      const activeSort = sortModel?.[0] || { field: "progressDate", sort: "asc" };

      return {
        pageIndex: 1,
        pageSize: rowCount || 10000,
        searchString,
        orderByAscending: activeSort.sort === "asc",
        orderCol: activeSort.field
      };
    },

    mapData: data =>
      data.map(item => ({
        "RAP RTI": item.rtiTitle || "",
        "Progress Date": item.progressDate || "",
        "Progress Percentage": item.progressPercentage || "",
        "Time In Course": item.timeInCourse || ""
      })),

    fileName: "RtiTracker.csv"
  });

  const getAllRapAssigned = async () => {
    setStatus(STAUTES.LOADING);
    const res = await getApi(`${GET_ALL_RTI_EMPLOYEERAPASSIGNED}/${id}`);
    const data = {
      rapTitle: res?.approvedJobTitle,
      currentProject: res?.projectName,
      rtiDescription: res?.raprtiGuid
    };
    setRapRtiData(prevData => {
      if (JSON.stringify(prevData) !== JSON.stringify(data)) {
        return data;
      }
      return prevData;
    });
    Object.keys(formConfig).forEach(field => setValue(field, data[field]));
    setStatus(STAUTES.IDLE);
  };

  const getAllRapRtiDropdown = async () => {
    setStatus(STAUTES.LOADING);
    const res = await getApi(`${GET_EMPLOYEERAP_RTI_DROPDOWN}/${id}`);
    setRapRtiDropDownData(res?.map(item => ({ label: item.label, value: item.value })));
    setStatus(STAUTES.IDLE);
  };

  useEffect(() => {
    getAllRapRti();
    if (isAddMode) onEdit();
    getAllRapRtiDropdown();
  }, [getAllRapRti]);

  const getAllCurrentCompletionRate = async selectedRTI => {
    setStatus(STAUTES.LOADING);
    const res = await getApi(`${GET_CURRENT_COMPLETION}/${id}/${selectedRTI}`);
    const completionRate = res?.progressPercentage;
    setValue("currentCompletionRate", completionRate);
    setRapRtiData(prevData => ({
      ...prevData,
      currentCompletionRate: completionRate
    }));
    setStatus(STAUTES.IDLE);
  };

  useEffect(() => {
    if (selectedRTI) getAllCurrentCompletionRate(selectedRTI);
  }, [selectedRTI]);

  const defaultFormProps = { control, formValues: rapRtiData, isAddMode, handleReadValueClick };

  return (
    <TabGridAndFormLayout
      showGrid={showGrid}
      title={`${actionData?.rtiProgressTrackerGuid ? "Edit" : "Add"} RTI Tracker`}
      backAction={() => resetForm()}
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      backLabel={"Go Back to All RTI Tracker"}
      canView={true}
      hideEdit={false}
      onSave={handleSubmit(onSave)}
      onCancel={onCancel}
      isSaving={isSaving}
    >
      {showGrid && (
        <ProGrid
          title="RTI Tracker"
          loading={status === STAUTES.LOADING}
          columns={columns}
          rows={rtisData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
          addButtonLabel="+ Add RTI Progress"
          hideExport={false}
          hideAddButton={false}
          addButtonAction={addButtonAction}
          options={{
            getRowId: row => row.rtiProgressTrackerGuid,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
          exportAction={handleDownloadCSV}
        />
      )}
      {!showGrid && (
        <>
          <ProTextInput {...defaultFormProps} {...formConfig.rapTitle} mode="read" />
          <ProTextInput {...defaultFormProps} {...formConfig.currentProject} mode="read" />
          <ProTextInput {...defaultFormProps} {...formConfig.currentCompletionRate} mode="read" />
          <ProSelectField {...defaultFormProps} {...formConfig.rtiDescription} options={rapRtiDropDownData} permission={fieldTruePermission} />
          <PercentField {...defaultFormProps} {...formConfig.progressPercentage} permission={fieldTruePermission} />
          <ProTextInput {...defaultFormProps} {...formConfig.timeinCourse} permission={fieldTruePermission} />
          <DateField {...defaultFormProps} {...formConfig.progressDate} permission={fieldTruePermission} />
        </>
      )}
    </TabGridAndFormLayout>
  );
}
